import React, {useContext} from 'react'
import {MetalColor, Finish, NotSure, NextSteps, Gemstones} from './shared'
import {Frame, FrameHeader, CheckMarkCard} from 'js/studios/shared/flows'
import {FlowFramesCtx} from 'js/studios/shared/unredux/contexts/flow'
import {KeyValueCtx} from 'js/studios/shared/unredux/contexts/key_value'
import {classNames} from 'js/studios/shared/utils'

const PENDANT_METAL_COLOR_CARDS = [
  {
    name: "silver_color",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/metal/silver.jpg`,
    label: "White Gold, Silver, Platinum"
  },
  {
    name: "yellow",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/metal/yellow-gold.jpg`,
    label: "Yellow Gold"
  },
  {
    name: "rose",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/metal/rose-gold.jpg`,
    label: "Rose Gold"
  },
  {
    name: "mixed_metals",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/metal/mixed.jpg`,
    label: "Mixed Metals"
  }
]

export function PendantMetalColor(props) {
  return <MetalColor cards={PENDANT_METAL_COLOR_CARDS} {...props} />
}

const PENDANT_FINISH_CARDS = [
  {
    name: "polished",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/finish/polished.jpg`,
    label: "Polished"
  },
  {
    name: "brushed",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/finish/brushed.jpg`,
    label: "Brushed"
  },
  {
    name: "oxidized",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/finish/oxidized.jpg`,
    label: "Oxidized"
  },
  {
    name: "enamel",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/finish/enamel.jpg`,
    label: "Enamel"
  },
]


export function PendantFinish(props) {
  return <Finish cards={PENDANT_FINISH_CARDS} {...props} />
}


const PENDANT_GEMSTONES_CARDS = [
  {
    name: "include_gemstones",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/gem/yes.jpg`,
    label: "Yes, I am interested in gemstone(s)"
  },
  {
    name: "all_metal_pendant",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/gem/no.jpg`,
    label: "No, I want an all-metal pendant"
  }
]

export function PendantGemstones(props) {
  return <Gemstones cards={PENDANT_GEMSTONES_CARDS} {...props} />
}

const SOLID_OR_CUTOUT_CARDS = [
  {
    name: "solid_raised",
    imgs: [
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/solid-raised-1.jpg`,
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/solid-raised-2.jpg`,
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/solid-raised-3.jpg`,
    ],
    label: "Solid (Raised)"
  },
  {
    name: "solid_recessed",
    imgs: [
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/solid-recessed-1.jpg`,
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/solid-recessed-2.jpg`,
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/solid-recessed-3.jpg`,
    ],
    label: "Solid (Recessed)"
  },
  {
    name: "cutout",
    imgs: [
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/cutout-1.jpg`,
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/cutout-2.jpg`,
      `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/solid-or-cutout/cutout-3.jpg`,
    ],
    label: "Cutout"
  }
]

export function SolidOrCutout() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {solidOrCutout, solidOrCutoutSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const
    isChecked = name => solidOrCutout === name,
    canSubmit = solidOrCutout || solidOrCutoutSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Solid or cutout?</FrameHeader>
    <p className="txt--center mb--lg">We can create your pendant as a solid shape with your design raised from the surface or recessed from the surface. Or, we can create a cutout in the shape of your design. Which of these options do you like best?</p>
    <div className="check-mark-cards-list mb--lg">
      {SOLID_OR_CUTOUT_CARDS.map(card => <CheckMarkCard
        key={card.name}
        imgs={card.imgs}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={solidOrCutoutSkip}
        onClick={boundSetter("solidOrCutout")}
        />
      )}
    </div>
    <NotSure field="solidOrCutoutSkip" fieldsToWipe={['solidOrCutout']} checked={solidOrCutoutSkip}>I’m not sure yet.</NotSure>
  </Frame>
}

const PENDANT_SHAPE_CARDS = [
  {
    name: "circle",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/circle.jpg`,
    label: "Circle"
  },
  {
    name: "heart",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/heart.jpg`,
    label: "Heart"
  },
  {
    name: "square",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/square.jpg`,
    label: "Square"
  },
  {
    name: "rectangle",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/rectangle.jpg`,
    label: "Rectangle",
    smallText: true
  },
  {
    name: "shield",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/shield.jpg`,
    label: "Shield"
  },
  {
    name: "cross",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/cross.jpg`,
    label: "Cross"
  },
  {
    name: "oval",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/oval.jpg`,
    label: "Oval"
  },
  {
    name: "letters",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/letters.jpg`,
    label: "Letters"
  },
  {
    name: "puzzle",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/puzzle.jpg`,
    label: "Puzzle"
  },
  {
    name: "custom",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/shape/custom.jpg`,
    label: "Custom"
  },
]

export function PendantShape() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {pendantShape, pendantShapeSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const
    isChecked = name => pendantShape === name,
    canSubmit = pendantShape || pendantShapeSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Choose a pendant shape.</FrameHeader>
    <p className="txt--center mb--lg">Circular pendants are our most popular choice, but there’s no wrong answer when it comes to shape! Since our necklaces are fully custom, you aren't limited to the examples - we can create any shape you like.</p>
    <div className="check-mark-cards-list mb--lg">
      {PENDANT_SHAPE_CARDS.map(card => <CheckMarkCard
        key={card.name}
        imgs={[card.img]}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={pendantShapeSkip}
        onClick={boundSetter("pendantShape")}
      />)}
    </div>
    <NotSure field="pendantShapeSkip" fieldsToWipe={['pendantShape']} checked={pendantShapeSkip}>I’m not sure yet.</NotSure>
  </Frame>
}

export function PendantSize() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {pendantSize, pendantSizeSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const canSubmit = pendantSize || pendantSizeSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Choose a pendant size.</FrameHeader>
    <p className="txt--center mb--lg">
      How large are you envisioning this pendant? Popular smaller sizes are 0.75" (like a US penny)
      and 1" (like a US quarter).  If you're looking for something more substantial &mdash; 1.25" or larger is a good bet.
    </p>
    <div className="pendant-size-demo mb--lg">
      <img className="pendant-size-demo__bg" src="//images.custommade.com/d2GgKlQbWsfPYks7DU_KpHqxDK8=/custommade-attachments/99aedd0cf75b965__7_pendant_size_gray_silhouette_01.png" role="presentation" alt="" />
      <div className={classNames("pendant-size-demo__pendant", `pendant-size-demo__pendant--size-${pendantSize?.replace?.(".", "_")?.replace?.(" in", "") || "none"}`)}>
        <img src="//images.custommade.com/ERzvwy9zTqJimvnKJzOKHWjF5WU=/custommade-attachments/3e4b684967ae665__7_pendant_size_20027faf274151e_2.png" role="presentation" alt="" />
      </div>
    </div>
    <select aria-label="Pendant size" className="field--md mb--1" onChange={boundSetter("pendantSize")} value={pendantSize || ""} disabled={pendantSizeSkip}>
      <option value="" disabled>Choose pendant size</option>
      <option value="0.5 in">Petite (0.5")</option>
      <option value="0.75 in">Small (0.75")</option>
      <option value="1 in">Standard (1")</option>
      <option value="1.25 in">Large (1.25")</option>
      <option value="1.5 in">Extra Large (1.5")</option>
    </select>
    <NotSure field="pendantSizeSkip" fieldsToWipe={['pendantSize']} checked={pendantSizeSkip}>I’m not sure yet.</NotSure>
  </Frame>
}

const PENDANT_CHAIN_LENGTHS = {
  "14": "//images.custommade.com/B1ySzkjjuKLkFx0-KfqUDjpojSo=/custommade-attachments/1b81a1476c41105__oval_copy.png",
  "16": "//images.custommade.com/WqU7dV71WKQBofo9qwVu5dhbVzM=/custommade-attachments/5e3f01db4c89cf3__oval.png",
  "18": "//images.custommade.com/bu6unIMlbamLfLj6QBQWchPMktg=/custommade-attachments/a24ccc705b24e45__oval_copy_2.png",
  "20": "//images.custommade.com/eVJ6VourGMgVKs2zDC7VpK0gi64=/custommade-attachments/fe865efa77695b1__oval_copy_3.png",
  "22": "//images.custommade.com/U9GLZtwrp9O6aN5JxRN06mKtkas=/custommade-attachments/ca00e0f3d7cea48__oval_copy_4.png",
  "24": "//images.custommade.com/SVOyqx9-iKMTffAktNQuMy2P6OQ=/custommade-attachments/dc0ed4ecf933a9d__oval_copy_5.png",
  "30": "//images.custommade.com/5DpY_YpTaYfPa1eTSRochFhD-xU=/custommade-attachments/b2e44c3f451c9d9__oval_copy_6.png",
  "36": "//images.custommade.com/9uMWLJ3P7Z3IYvswhKmEeFCrqUg=/custommade-attachments/9940d2dc71f7d64__oval_copy_7.png",
}

export function PendantChainLength() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {chainLength, chainLengthSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const canSubmit = chainLength || chainLengthSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Choose a chain length.</FrameHeader>
    <p className="txt--center mb--lg">How long would you like your chain to be?</p>
    <div className="chain-length-demo mb--lg">
      <img className="chain-length-demo__bg" src="//images.custommade.com/UqgBMQuwCSesOUVjZUB6uOrGPc0=/custommade-attachments/8d64818948d4cbc__8_chain_length_gray_silhouette_01.png" role="presentation" alt="" />
      {Object.keys(PENDANT_CHAIN_LENGTHS).map(chainLength => <img
        key={chainLength}
        className={classNames("chain-length-demo__chain", `chain-length-demo__chain--${chainLength}`, {"chain-length-demo__chain--active": chainLength?.replace?.(" in", "") === chainLength})}
        src={PENDANT_CHAIN_LENGTHS[chainLength]}
        role="presentation" alt=""
        />
      )}
    </div>
    <select aria-label="Chain length" className="field--md mb--1" onChange={boundSetter("chainLength")} value={chainLength || ""} disabled={chainLengthSkip}>
      <option value="" disabled>Choose chain length</option>
      <option value="14 in">14"</option>
      <option value="16 in">16"</option>
      <option value="18 in">18"</option>
      <option value="20 in">20"</option>
      <option value="22 in">22"</option>
      <option value="24 in">24"</option>
      <option value="30 in">30"</option>
      <option value="36 in">36"</option>
    </select>
    <NotSure field="chainLengthSkip" fieldsToWipe={['chainLength']} checked={chainLengthSkip}>I’m not sure yet.</NotSure>
  </Frame>
}

const PENDANT_CHAIN_STYLES = [
  {
    name: "rope",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/chain/rope.jpg`,
    label: "Rope"
  },
  {
    name: "cable",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/chain/cable.jpg`,
    label: "Cable"
  },
  {
    name: "rolo",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/chain/rolo.jpg`,
    label: "Rolo"
  },
  {
    name: "curb",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/chain/curb.jpg`,
    label: "Curb"
  },
  {
    name: "thick_cable",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/chain/thick-cable.jpg`,
    label: "Thick Cable"
  },
  {
    name: "figaro",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/pendant/chain/figaro.jpg`,
    label: "Figaro"
  },
]

export function PendantChainStyles() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {chainStyle, chainStyleSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const
    isChecked = name => chainStyle === name,
    canSubmit = chainStyle || chainStyleSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Choose a chain style.</FrameHeader>
    <p className="txt--center mb--lg">Which style do you prefer?</p>
    <div className="check-mark-cards-list mb--lg">
      {PENDANT_CHAIN_STYLES.map(card => <CheckMarkCard
        key={card.name}
        imgs={[card.img]}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={chainStyleSkip}
        onClick={boundSetter("chainStyle")}
      />)}
    </div>
    <NotSure field="chainStyleSkip" fieldsToWipe={['chainStyle']} resetValue={[]} checked={chainStyleSkip}>I’m not sure yet.</NotSure>
  </Frame>
}

const PENDANT_NEXT_STEPS_CARDS = [
  {
    name: "price",
    description: "Our design experts will prepare some initial quotes for you to review as a starting point for your necklace.",
    label: "I’d like to discuss price first."
  },
  {
    name: "questions",
    description: "If you have other things you’d like to discuss before our team gets started, let us know - one of our consultants will get in touch.",
    label: "I have questions to ask or other details to share first."
  },
  {
    name: "looking_around",
    description: "Just looking around for now? We’ll hold onto your details and you can let us know when you’re ready to get started.",
    label: "I’m not ready for a consultation yet."
  }
]

export const PendantNextSteps = () => <NextSteps
  cards={PENDANT_NEXT_STEPS_CARDS}
  subheader="We have all the information we need to get started! We can start by talking more about the design of your necklace, or we can get right to some price estimates - which would you like to do first?"
  />
